import {
  Button,
  Content,
  Flex,
  fromTheme,
  Heading,
  IconButton,
  StrokeIcon,
  Textarea,
  useToast,
} from '@acsbe/ui';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDomainSelection, useMerchantSession } from '../../context';
import type { UserFeedback } from '../../type/UserFeedback';

export const NegativePath = () => {
  const [value, setValue] = useState('');
  const [shouldSubmitFeedback, setShouldSubmitFeedback] = useState(false);
  const { postFeedback } = useDomainSelection();
  const { userFeedback, setUserFeedback } = useMerchantSession();
  const toast = useToast();

  const handleCancel = () => {
    postFeedback();
  };

  const handleClick = () => {
    setUserFeedback((prev: UserFeedback | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        feedbackComment: value.trim(),
      };
    });
    setShouldSubmitFeedback(true);
  };

  useEffect(() => {
    if (shouldSubmitFeedback && userFeedback?.feedbackComment) {
      postFeedback();
      setShouldSubmitFeedback(false);
      toast(<>Thanks for your feedback</>, {
        scale: 'base',
        duration: 3000,
        icon: 'Check',
      });
    }
  }, [
    shouldSubmitFeedback,
    userFeedback?.feedbackComment,
    postFeedback,
    toast,
  ]);

  return (
    <Flex
      align="center"
      scale="ghost"
      paddingBlockStart={10}
      width="456px"
      paddingBlockEnd={8}
      paddingInline={8}
      gap={8}
    >
      <StyledIconButton
        as="div"
        padding={6}
        aria-label="Thumbs Down"
        radius="round"
        scale="danger"
      >
        <StrokeIcon name="ThumbsDown" size={32} />
      </StyledIconButton>
      <Heading
        level={3}
        align="center"
        scale="base"
        emphasis="high"
        lineHeight={1.25}
      >
        We're sorry to hear that
      </Heading>
      <Flex align="flex-start" gap={2} width="432px">
        <Content scale="base" emphasis="none" size="md" lineHeight={1.5}>
          How can we do better?
        </Content>
        <StyledTextarea
          placeholder="Tell us what didn't work for you?"
          onChange={(e) => setValue(e.target.value)}
          value={value}
        />
      </Flex>
      <Flex row align="center" justify="flex-end" gap={3} self="stretch">
        <Button
          scale="base"
          size="lg"
          variant="secondary"
          data-mixpanel="negative-feedback-cancel"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          scale="primary"
          size="lg"
          variant="primary"
          disabled={value.trim() === ''}
          data-mixpanel="negative-feedback-submit"
          onClick={handleClick}
        >
          Submit
        </Button>
      </Flex>
    </Flex>
  );
};

const StyledIconButton = styled(IconButton)`
  cursor: default;
  --fill: ${fromTheme('palette.scales.danger.background.static')};
  --text: ${fromTheme('palette.scales.danger.text.static')};
  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    --fill: ${fromTheme('palette.scales.danger.background.static')};
    --text: ${fromTheme('palette.scales.danger.text.static')};
  }
`;

const StyledTextarea = styled(Textarea)`
  height: 80px !important;
`;
